// src/components/About.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HiCheckCircle, HiUsers, HiLightningBolt, HiHeart } from 'react-icons/hi';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const stats = [
    { icon: HiUsers, number: '10+', label: 'Happy Clients' },
    { icon: HiLightningBolt, number: '15+', label: 'Projects Completed' },
    { icon: HiCheckCircle, number: '6+', label: 'Years Experience' },
    { icon: HiHeart, number: '24/7', label: 'Support' }
  ];

  const features = [
    'Industry-leading expertise in modern technologies',
    'Agile development methodology for faster delivery',
    'Dedicated support team for ongoing maintenance',
    'Scalable solutions that grow with your business',
    'Security-first approach in all our implementations',
    'Transparent communication throughout the project'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const imageVariants = {
    hidden: { x: 50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section id="about" className="section-padding bg-white relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-24 -left-24 w-96 h-96 bg-primary-100 rounded-full opacity-30"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-24 -right-24 w-80 h-80 bg-secondary-100 rounded-full opacity-25"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -120, 0],
            x: [0, -40, 0],
            y: [0, 40, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-accent-100 rounded-full opacity-20"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360],
            x: [0, 30, 0],
            y: [0, -20, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
        />
        
        {/* Floating Particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary-300 rounded-full opacity-40"
            style={{
              left: `${20 + i * 10}%`,
              top: `${10 + i * 8}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0.2, 0.8, 0.2]
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.5
            }}
          />
        ))}
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid lg:grid-cols-2 gap-16 items-center"
        >
          {/* Content */}
          <motion.div variants={itemVariants}>
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              whileInView={{ 
                scale: [0.9, 1.05, 1],
                opacity: [0, 1, 1]
              }}
              transition={{ duration: 0.8 }}
            >
              About <span className="text-gradient">US</span>
            </motion.h2>
            
            <motion.p 
              className="text-lg text-neutral-700 mb-8 leading-relaxed" // Changed text-gray-600 to text-neutral-700
              whileInView={{ 
                y: [30, 0],
                opacity: [0, 1]
              }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              With over a decade of experience in the technology industry,<span className=' text-xl text-primary-500'> EFA Tech Solutions</span>  has been at the forefront of digital innovation. We specialize in creating cutting-edge solutions that transform businesses and drive growth in the digital age.
            </motion.p>

            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileInView={{ 
                    x: [-50, 0],
                    opacity: [0, 1]
                  }}
                  transition={{ 
                    duration: 0.6,
                    delay: index * 0.1
                  }}
                  className="flex items-start space-x-3"
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <HiCheckCircle className="w-6 h-6 text-primary-500 flex-shrink-0 mt-0.5" />
                  </motion.div>
                  <span className="text-neutral-700">{feature}</span> {/* Changed text-gray-700 to text-neutral-700 */}
                </motion.div>
              ))}
            </div>

            {/* Stats */}
            <motion.div
              variants={containerVariants}
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileInView={{ 
                    scale: [0.8, 1.1, 1],
                    opacity: [0, 1]
                  }}
                  transition={{ 
                    duration: 0.6,
                    delay: index * 0.1
                  }}
                  className="text-center group"
                >
                  <motion.div
                    whileHover={{ scale: 1.3, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="hover-glow"
                  >
                    <stat.icon className="w-8 h-8 text-primary-500 mx-auto mb-2 pulse-glow" />
                  </motion.div>
                  <motion.div 
                    className="text-2xl font-bold text-neutral-900" // Changed text-gray-900 to text-neutral-900
                    whileHover={{ scale: 1.1 }}
                  >
                    {stat.number}
                  </motion.div>
                  <div className="text-sm text-neutral-600">{stat.label}</div> {/* Changed text-gray-600 to text-neutral-600 */}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Enhanced Image Section */}
          <motion.div variants={imageVariants} className="relative">
            <div className="relative z-10">
              <motion.div 
                className="bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl p-8 shadow-2xl" // Updated gradient
                whileInView={{ 
                  scale: [0.9, 1.02, 1],
                  rotate: [0, 1, 0]
                }}
                transition={{ duration: 1 }}
              >
                <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                  <div className="space-y-4">
                    <motion.div 
                      className="flex items-center space-x-4"
                      whileInView={{ x: [-30, 0], opacity: [0, 1] }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <motion.div 
                        className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center"
                        whileHover={{ scale: 1.1, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                      >
                        <HiLightningBolt className="w-6 h-6 text-white" />
                      </motion.div>
                      <div>
                        <h3 className="text-white font-semibold">Innovation First</h3>
                        <p className="text-neutral-200 text-sm">Cutting-edge solutions</p> {/* Changed text-gray-200 to text-neutral-200 */}
                      </div>
                    </motion.div>
                    
                    <motion.div 
                      className="flex items-center space-x-4"
                      whileInView={{ x: [-30, 0], opacity: [0, 1] }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                    >
                      <motion.div 
                        className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center"
                        whileHover={{ scale: 1.1, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                      >
                        <HiUsers className="w-6 h-6 text-white" />
                      </motion.div>
                      <div>
                        <h3 className="text-white font-semibold">Expert Team</h3>
                        <p className="text-neutral-200 text-sm">Skilled professionals</p> {/* Changed text-gray-200 to text-neutral-200 */}
                      </div>
                    </motion.div>
                    
                    <motion.div 
                      className="flex items-center space-x-4"
                      whileInView={{ x: [-30, 0], opacity: [0, 1] }}
                      transition={{ duration: 0.6, delay: 0.6 }}
                    >
                      <motion.div 
                        className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center"
                        whileHover={{ scale: 1.1, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                      >
                        <HiHeart className="w-6 h-6 text-white" />
                      </motion.div>
                      <div>
                        <h3 className="text-white font-semibold">Client Focus</h3>
                        <p className="text-neutral-200 text-sm">Your success is our priority</p> {/* Changed text-gray-200 to text-neutral-200 */}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Enhanced Background Elements */}
            <motion.div 
              className="absolute -top-8 -right-8 w-80 h-80 bg-primary-200 rounded-full opacity-30"
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360],
                x: [0, 20, 0],
                y: [0, -10, 0]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div 
              className="absolute -bottom-8 -left-8 w-60 h-60 bg-secondary-200 rounded-full opacity-25"
              animate={{
                scale: [1, 1.3, 1],
                rotate: [360, 180, 0],
                x: [0, -15, 0],
                y: [0, 15, 0]
              }}
              transition={{
                duration: 16,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;